import { isMobile } from 'react-device-detect';
import { isConfigFlagEnabled } from '../../config';
import { getNewsItemHash, getNewsItems } from '../../scenes/entry/news/newsItems';
import { removePixiAppFromWindow } from '../../scenes/poker/pixiAppMountHandler';
import { isRoomSceneType } from '../../types/sceneTypes';
import { setBugsnagScene } from '../../utils/bugsnagUtils';
import { assertUnreachable } from '../../utils/tsUtils';
import { isDebugPanelEnabled } from '../../utils/utils';
import { clearChatItems } from '../actions/chatActions';
import { clearLobbyScene } from '../actions/lobbyActions';
import { clearPokerScene } from '../actions/pokerActions';
import { selectIsSideBetsPanelEnabled } from '../reducers/selfDataReducer';
import { clearAnimationsState } from './animationsActions';
import { clearBombsState } from './bombsActions';
import { clearCommonState } from './commonActions';
import { clearCreateGameDialog } from './createGameActions';
import { clearDirectMessagesState } from './directMessagesActions';
import { clearDrawerState } from './drawerActions';
import { clearFooterAlertState } from './footerAlertActions';
import { clearGameDimensionsState } from './gameDimensionsActions';
import { clearHistoryState } from './historyActions';
import { addMessageBox, clearMessageBoxesState } from './messageBoxesActions';
import { clearMttState } from './mttActions';
import { clearMttStagingState } from './mttStagingActions';
import { clearPanelsState, thunkSetPanelTypes } from './panelsActions';
import { clearBubblesState } from './playerBubblesActions';
import { clearPlayerInfoState } from './playerInfoActions';
import { clearReverifyState } from './reverifyActions';
import { setIsAvatarEditorOpen } from './selfAvatarActions';
import { setLoadingIsDone } from './sessionActions';
import { thunkClearSideBetsState } from './sideBetsActions';
import { clearToastsState } from './toastsActions';
export const SET_SCENE = 'SET_SCENE';
/**
 * When initializing a specific scene such as the lobby, check if there are news items that the user (likely)
 * hasn't seen yet, comparing the last-read news item hash stored in localStorage with what is generated from
 * the latest news item stored in the client code.
 */
function thunkMaybeShowLatestNews() {
    return function (dispatch, getState) {
        const newsItems = getNewsItems();
        const latestNewsItem = newsItems.length ? newsItems[newsItems.length - 1] : undefined;
        if (!latestNewsItem) {
            // no news items, nothing to do
            return;
        }
        const state = getState();
        const lastNewsItemHash = state.selfData.userClientSettings.LAST_NEWS_ITEM_HASH.value;
        const shouldShowLatestNews = !lastNewsItemHash || lastNewsItemHash !== getNewsItemHash(latestNewsItem);
        if (shouldShowLatestNews) {
            dispatch(addMessageBox({ type: 'news', lastNewsItemHash }));
        }
    };
}
function setScene(scene) {
    return {
        type: SET_SCENE,
        payload: scene
    };
}
function getPanelTypes(roomSceneType, getState) {
    const panelTypes = [];
    switch (roomSceneType) {
        case 'lobby': {
            panelTypes.push('chat', 'players', 'friends', 'directMessages', 'playerInfo', 'self');
            break;
        }
        case 'poker': {
            panelTypes.push('chat', 'pokerRoomInfo', 'players', 'friends', 'directMessages', 'playerInfo', 'self', 'history', 'bombs');
            if (selectIsSideBetsPanelEnabled(getState())) {
                panelTypes.push('sideBets');
            }
            break;
        }
        case 'mttStaging': {
            panelTypes.push('chat', 'players', 'friends', 'directMessages', 'playerInfo', 'self');
            break;
        }
        default: {
            assertUnreachable(roomSceneType);
        }
    }
    if (isDebugPanelEnabled()) {
        panelTypes.push('debug');
    }
    if (isConfigFlagEnabled('adminPanel') && getState().selfData.isAdmin) {
        panelTypes.push('admin');
    }
    return panelTypes;
}
export function thunkSetScene(requestedScene, socketSend) {
    return function (dispatch, getState) {
        const state = getState();
        const currentSceneType = state.scene.sceneType;
        // make sure scene states are cleared when leaving:
        const currentRoomSceneType = state.scene.roomSceneType;
        if (currentRoomSceneType) {
            switch (currentRoomSceneType) {
                case 'lobby': {
                    dispatch(clearLobbyScene());
                    dispatch(clearCreateGameDialog());
                    break;
                }
                case 'poker': {
                    dispatch(clearPokerScene());
                    dispatch(clearGameDimensionsState());
                    dispatch(clearHistoryState());
                    dispatch(clearAnimationsState());
                    dispatch(clearBubblesState());
                    break;
                }
                case 'mttStaging': {
                    dispatch(clearMttStagingState());
                    break;
                }
                default: {
                    assertUnreachable(currentRoomSceneType);
                }
            }
        }
        dispatch(clearBombsState());
        dispatch(clearMessageBoxesState());
        dispatch(clearDrawerState());
        dispatch(clearChatItems());
        dispatch(clearPlayerInfoState());
        dispatch(clearToastsState());
        dispatch(clearFooterAlertState());
        dispatch(clearCommonState());
        dispatch(clearDirectMessagesState());
        dispatch(clearMttState());
        dispatch(clearPanelsState());
        dispatch(clearReverifyState());
        dispatch(thunkClearSideBetsState());
        dispatch(setIsAvatarEditorOpen(false));
        function getNewScene() {
            const currentScene = state.scene.sceneType;
            if (requestedScene !== currentScene) {
                return requestedScene;
            }
            else {
                // scene type is the same, need to ensure the change is recognized and new scene/room initializes
                if (requestedScene === 'poker') {
                    return 'roomToPoker';
                }
                else if (requestedScene === 'mttStaging') {
                    return 'roomToMttStaging';
                }
                else {
                    // TODO: Support lobby-to-lobby at some point?
                    throw new Error(`moving from scene ${currentScene} to ${requestedScene} not supported`);
                }
            }
        }
        const newSceneType = getNewScene();
        if (isRoomSceneType(newSceneType)) {
            const panelTypes = getPanelTypes(newSceneType, getState);
            switch (newSceneType) {
                case 'poker': {
                    // on mobile, start poker rooms with the panel closed:
                    const activePanelType = isMobile ? undefined : 'chat';
                    dispatch(thunkSetPanelTypes(panelTypes, socketSend, true, activePanelType));
                    break;
                }
                case 'lobby': {
                    // on mobile, start lobby with the panel closed:
                    const activePanelType = isMobile ? undefined : 'chat';
                    dispatch(thunkSetPanelTypes(panelTypes, socketSend, true, activePanelType));
                    dispatch(thunkMaybeShowLatestNews());
                    break;
                }
                case 'mttStaging': {
                    dispatch(thunkSetPanelTypes(panelTypes, socketSend, false, 'chat'));
                    break;
                }
                default: {
                    assertUnreachable(newSceneType);
                }
            }
        }
        setBugsnagScene(newSceneType, getState().panels.activePanelType);
        /**
         * When entering a poker scene, ensure any existing pixi app reference for this instance is cleared first.
         * These references are stored on the "top" window so that Cypress tests, which render the app in an iframe,
         * can reference the active pixi app(s).
         * However, if a test is running and on the poker scene when that test ends and a new test begins, the top window
         * can hold a stale reference because no clean-up of the pixi app reference had a chance to run.
         * A subsequent test could load the poker scene and then try interacting with the stale pixi app reference before
         * it has a chance to be updated when <PokerTableStage> initializes. By detaching any reference before loading the
         * poker scene, this test flakiness problem should be averted:
         */
        removePixiAppFromWindow(state.instanceId.instanceId);
        dispatch(setScene(newSceneType));
        if (currentSceneType === 'loading') {
            // after redux is told we have left the loading scene, make sure the full loading scene is not used again this session:
            dispatch(setLoadingIsDone());
        }
    };
}
